import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import StructuredInsightsPanel from "../components/StructuredInsightsPanel";
import api from "../lib/api";
import { Sparkles, Send } from "lucide-react";
import { toast } from "sonner";

export default function AIInsights() {
  const [recs, setRecs] = useState([]);
  const [q, setQ] = useState("");
  const [investigation, setInvestigation] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/ai/recommendations").then((r) => setRecs(r.data));
  }, []);

  const ask = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/investigate", { question: q });
      setInvestigation(data);
    } catch (e) { toast.error("AI request failed"); }
    finally { setLoading(false); }
  };

  const toneFor = (u) => u === "high" ? "critical" : u === "moderate" ? "warning" : "success";

  return (
    <Layout title="AI Insights" subtitle="Structured signals from MongoDB · severity-ordered">
      {/* Structured insights — deterministic engine */}
      <div className="mb-6">
        <StructuredInsightsPanel />
      </div>

      {/* Investigation */}
      <div className="bg-card border border-border border-t-2 border-t-[hsl(var(--ai))] rounded-sm p-5 mb-6" data-testid="ai-investigate-panel">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-[hsl(var(--ai))]" />
          <div className="label-mono text-[hsl(var(--ai))]">Research & Investigation</div>
        </div>
        <div className="font-heading text-lg mb-3 tracking-tight">Ask a question about your accounts</div>
        <div className="flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="e.g. Why did CPL rise in the last 7 days?"
            data-testid="ai-question-input"
            className="flex-1 h-10 px-3 border border-border rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ai))] focus:border-[hsl(var(--ai))]"
          />
          <button
            onClick={ask}
            disabled={loading}
            data-testid="ai-ask-btn"
            className="h-10 px-4 bg-[hsl(var(--ai))] text-white text-sm font-medium rounded-sm hover:bg-[hsl(var(--ai))]/90 flex items-center gap-2"
          >
            <Send className="w-3.5 h-3.5" /> {loading ? "Thinking…" : "Ask"}
          </button>
        </div>

        {investigation && (
          <div className="mt-5 pt-5 border-t border-border" data-testid="ai-investigation-result">
            <div className="label-mono mb-2">Reasoning</div>
            <ol className="text-sm space-y-1 list-decimal pl-5 text-muted-foreground mb-4">
              {investigation.reasoning.map((r, i) => <li key={i}>{r}</li>)}
            </ol>
            <div className="label-mono mb-2">Conclusion</div>
            <div className="text-sm font-medium mb-4">{investigation.conclusion}</div>
            {investigation.evidence?.length > 0 && (
              <div className="flex gap-3">
                {investigation.evidence.map((e, i) => (
                  <div key={i} className="border border-border rounded-sm p-3 text-xs flex-1">
                    <div className="label-mono mb-1">{e.metric} · {e.period}</div>
                    <div className="font-semibold tabular">{e.before} → {e.after}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ai-recommendations-grid">
        {recs.map((r, i) => (
          <div key={i} className="bg-card border border-border border-l-2 border-l-[hsl(var(--ai))] rounded-sm p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="font-heading font-medium text-sm truncate">{r.campaign_name}</div>
              <StatusBadge tone={toneFor(r.urgency)}>{r.urgency}</StatusBadge>
            </div>
            <div className="text-xs text-muted-foreground mb-3">{r.context}</div>
            <div className="space-y-1.5 mb-3">
              {r.findings.map((f, j) => (
                <div key={j} className="text-sm"><span className="label-mono mr-2">Finding</span>{f}</div>
              ))}
            </div>
            <div className="space-y-1.5">
              {r.recommendations.map((rec, j) => (
                <div key={j} className="text-sm font-medium"><span className="label-mono mr-2 text-[hsl(var(--ai))]">Action</span>{rec}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
